import { postApiB2bApplicationByIdFormAmend } from '/api/sdk.gen';
import { getFormState } from '/business/shared/state/actions/application';
import { refreshSession } from '/business/shared/state/actions/auth';
import normaliseError, { toNormaliseErrorInput } from '/shared/functions/normaliseError';
import { showModal } from '/shared/state/actions/modal';
import type { PostApiB2bApplicationByIdFormAmendResponse } from '/api';
import type { BasketItem } from '/business/shared/state/reducers/basket';
import type { Dispatch } from '/business/shared/state/actions/types';

type SaveBasketItem = BasketItem & {
  id?: number | null;
  isBulk?: boolean;
};

/**

 * @param basketItem
 * @param applicationId
 * @param isDelete
 * @param isBulk
 * @returns {(function(*): Promise<boolean|null|*|number>)|*}
 */
const saveItem =
  ({
    basketItem,
    applicationId,
    isDelete = false,
    isBulk = false,
  }: {
    basketItem: SaveBasketItem | SaveBasketItem[];
    applicationId: number | string | null;
    isDelete?: boolean;
    isBulk?: boolean;
  }) =>
  async (dispatch: Dispatch) => {
    if (!applicationId) {
      return;
    }

    try {
      if (isBulk && Array.isArray(basketItem) && basketItem.length === 0) {
        return;
      }

      await dispatch({ type: 'BSK_SAVE_ATTEMPT_BEGUN' });
      await dispatch(refreshSession({ force: true }));

      const itemForInspection = Array.isArray(basketItem) ? basketItem[0] : basketItem;
      const itemType = itemForInspection.type;
      if (!itemType) {
        return;
      }

      const { data: res } = await postApiB2bApplicationByIdFormAmend({
        path: { id: String(applicationId) },
        body: {
          basketItem,
          itemType,
          isDelete,
          isBulk,
        },
      });
      if (!res) {
        return;
      }
      const { revalidate = false, revalidateSpecific = [] }: PostApiB2bApplicationByIdFormAmendResponse = res;
      /*
    To handle what to revalidate custom/supplier data, we inspect the original payload
     */
      if (revalidate) {
        await dispatch(getFormState(applicationId));
        await dispatch({ type: 'BSK_SAVE_ATTEMPT_COMPLETE', partial: false });
      } else if (revalidateSpecific.length > 0) {
        await dispatch({ type: 'BSK_SAVE_ATTEMPT_COMPLETE', partial: true });

        await dispatch(getFormState(applicationId, revalidateSpecific));
      }
    } catch (error) {
      const { code } = normaliseError(toNormaliseErrorInput(error));
      await dispatch({ type: 'BASKET_ADD_REJECTED', error });
      await dispatch({ type: 'BSK_SAVE_ATTEMPT_REJECTED', error });

      if (code === 'ERRNOANSWERVALUE') {
        // NOOP - error is handled by the form so no action required here
      } else {
        dispatch(
          showModal({
            name: 'UNKNOWN_ERROR',
            isBlocking: true,
            metadata: {
              message: 'There was an issue syncing your form with the server.',
              error: toNormaliseErrorInput(error),
            },
          }),
        );
      }

      throw error;
    }
  };

export default saveItem;
